import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Spotlight } from '@forecastify/ui';
import { projectSchema } from '@forecastify/shared';
import { useT } from '../i18n';
import { useSession } from '../session';
import { parseList } from '../screens/permissions-hook';
import { TourDriver, rememberStorage, type TourTiming } from './tour-driver';
import {
  TOUR_STORAGE_PREFIX,
  chapterOf,
  tourSteps,
  type TourContext,
  type TourStep,
} from './tour-steps';

export type TourEnd = 'finished' | 'closed';

/** The three fields the tour chooses a project by — and only those, so nothing else can refuse it. */
const PROJECT_PICK = projectSchema.pick({ id: true, keyPrefix: true, pmMemberId: true });

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ F-2 · the tour, walking its chapters over the real app.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * For each step, `TourDriver` brings the app to it — its page, its clicks, closing what the step
 * before it opened — and the card points once it has. Every door is the same call: Next, Previous,
 * Skip this section, a step skipped on the way, the X.
 *
 * ⚠️⚠️ **THE CARD STAYS WHILE THE TOUR WORKS, AND ITS CONTROLS STAY LIVE.** It used to vanish for as
 * long as the tour clicked, so Previous, Skip and the X seemed to do nothing and somebody could be
 * left looking at a scrim. Pressing one now cancels the work in flight — no click of the old step
 * lands after it — and starts the new step's.
 *
 * ⚠️⚠️ **THE STEPS ARE FIXED WHEN THE TOUR STARTS.** The sidebar read refreshes on every live event,
 * and a step list recomputed mid-tour could drop the step somebody is standing on.
 *
 * ⚠️ **One project, chosen once**: the first project the person manages when their `project.manage`
 * is their own; otherwise the demo's webshop, or the first project at all. None, no project chapter.
 *
 * ⚠️ **Below 900px every step is a centred card.** The rail is a drawer there.
 */
export function Tour({
  context,
  memberId,
  navigate,
  onEnd,
  waitMs,
  timing,
}: {
  /** How long a step waits for its target before it centres or skips. Tests shorten it. */
  readonly waitMs?: number;
  /** How long the tour waits on the app. Tests shorten it. */
  readonly timing?: TourTiming;
  /** Everything but the project, which the tour finds for itself. */
  readonly context: Omit<TourContext, 'projectId'>;
  readonly memberId: string | null;
  /** Workspace-relative navigation — `app.tsx`'s `go`. */
  readonly navigate: (path: string) => void;
  readonly onEnd: (how: TourEnd) => void;
}): ReactNode {
  const t = useT();
  const session = useSession();
  const [steps, setSteps] = useState<TourStep[] | null>(null);
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(true);
  const [centred, setCentred] = useState(false);

  /* What the tour started with, held still, and the callbacks, held current. */
  const [start] = useState(() => ({ context, memberId }));
  const live = useRef({ session, navigate, onEnd });
  live.current = { session, navigate, onEnd };

  const [driver] = useState(
    () =>
      new TourDriver((path) => {
        live.current.navigate(path);
      }, timing),
  );
  /* The Home sections' open/shut, as the person left them. */
  const [restore] = useState(() => rememberStorage(TOUR_STORAGE_PREFIX));
  const ending = useRef(false);
  const direction = useRef<1 | -1>(1);
  const all = useRef<TourStep[] | null>(null);
  all.current = steps;

  useEffect(() => {
    const alive = { current: true };
    void (async () => {
      const response = await live.current.session.request('/projects?limit=50').catch(() => null);
      const projects =
        (response?.ok === true
          ? parseList(PROJECT_PICK, await response.json().catch(() => null))
          : null) ?? [];
      const own =
        start.context.scopeOf('project.manage') === 'own'
          ? projects.find((candidate) => candidate.pmMemberId === start.memberId)
          : undefined;
      const chosen =
        own ?? projects.find((candidate) => candidate.keyPrefix === 'WEB') ?? projects[0];
      if (alive.current) setSteps(tourSteps({ ...start.context, projectId: chosen?.id ?? null }));
    })();
    return () => {
      alive.current = false;
    };
  }, [start]);

  /** The tour is over, by either door: close what it opened, put back what it changed, then say so. */
  const end = (how: TourEnd): void => {
    if (ending.current) return;
    ending.current = true;
    setBusy(true);
    void driver.finish().then(() => {
      restore();
      live.current.onEnd(how);
    });
  };

  /** Show step `at`, arriving from direction `by`. A step that cannot be reached is passed over. */
  const show = (at: number, by: 1 | -1): void => {
    const list = all.current;
    if (list === null || ending.current) return;
    if (at >= list.length) {
      end('finished');
      return;
    }
    /* Passing over the first step backwards: there is nothing before it, so go on forwards. */
    if (at < 0) {
      show(at + 2, 1);
      return;
    }
    const step = list[at] as TourStep;
    direction.current = by;
    setIndex(at);
    setBusy(true);
    setCentred(false);
    void driver.enter(step).then((outcome) => {
      if (outcome === 'cancelled' || ending.current) return;
      if (outcome === 'ready') setBusy(false);
      else show(at + by, by);
    });
  };
  const showLatest = useRef(show);
  showLatest.current = show;

  /* The first step, once the steps are known. An empty tour ends at once rather than drawing an
     empty card. */
  useEffect(() => {
    if (steps === null) return;
    if (steps.length === 0) live.current.onEnd('closed');
    else showLatest.current(0, 1);
  }, [steps]);

  /* Unmounted without ending — the workspace changed under it: stop, and leave nothing open. */
  useEffect(
    () => () => {
      if (ending.current) return;
      /* ⚠️ Fire-and-forget, so a failure here has nobody to report to: caught, or it surfaces as an
         unhandled rejection long after this component — or, in a test, the whole page — is gone. */
      void driver.finish().catch(() => undefined);
      restore();
    },
    [driver, restore],
  );

  const step = steps?.[index];
  if (steps === null || step === undefined) return null;

  const inChapter = steps.filter((candidate) => candidate.chapter === step.chapter);
  const position = `${t(chapterOf(step.chapter).title)} · ${String(inChapter.indexOf(step) + 1)} / ${String(inChapter.length)}`;
  const nextChapter = steps.findIndex(
    (candidate, at) => at > index && candidate.chapter !== step.chapter,
  );

  const narrow =
    typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 899px)').matches;

  return (
    <Spotlight
      open
      busy={busy}
      {...(waitMs === undefined ? {} : { waitMs })}
      target={narrow || centred ? null : step.target}
      side={step.side}
      title={t(step.title)}
      body={t(step.body)}
      index={index}
      total={steps.length}
      position={position}
      labels={{
        next: t('tour.next'),
        previous: t('tour.previous'),
        finish: t('tour.finish'),
        close: t('tour.close'),
        skipSection: t('tour.skipSection'),
        busy: t('tour.busy'),
        step: (current, all) => t('tour.step', { current, total: all }),
      }}
      onNext={() => {
        show(index + 1, 1);
      }}
      onPrevious={() => {
        show(index - 1, -1);
      }}
      onClose={() => {
        end('closed');
      }}
      {...(nextChapter === -1
        ? {}
        : {
            onSkipSection: () => {
              show(nextChapter, 1);
            },
          })}
      onTargetMissing={() => {
        if (step.ifMissing === 'center') setCentred(true);
        else show(index + direction.current, direction.current);
      }}
    />
  );
}
